<template>
    <section id="cast-eligibility" class="cast-eligibility-card card mb-3" aria-labelled-by="suporting-documents">
        <form
            v-if="castEligibility"
            ref="castEligibilityForm"
            class="card-body"
            v-click-outside="onClickOutside"
            @click="focusBlock"
        >
            <div class="card-heading">
                <h5 class="card-name" id="suporting-documents">
                    {{ $t('lib_supporting_documents') }}
                </h5>
            </div>
            <hr />
            <p
                class="block-error-feedback text-danger"
                v-if="blockErrorMessage(block.name)"
            >
                <font-awesome-icon icon="fas fa-exclamation-circle" />
                {{ $t(blockErrorMessage(block.name)) }}
            </p>
            <p
                class="block-error-feedback text-danger"
                v-if="blockErrorMessage(block.name, 'castEligibility')"
            >
                <font-awesome-icon icon="fas fa-exclamation-circle" />
                {{ $t(blockErrorMessage(block.name, 'castEligibility')) }}
            </p>
            <Message severity="warn" v-if="showWarning">
                {{ $t('val_not_eligible') }}
            </Message>
            <div class="d-flex wrap-ca-type">
                <div class="form-group mb-3 required-group" v-if="applicationTypeIsCa">
                    <label for="caApplyAs" class="form-label">
                        {{ $t('lib_cand_type') }}
                    </label>
                    <Dropdown
                        appendTo="self"
                        :showClear="true"
                        inputId="castEligibilityCA"
                        v-model="castEligibility.contractAgentCastEligibilityCategory"
                        :class="{
                            'is-invalid': errorMessage(
                                'contractAgentCastEligibilityCategory',
                                block.name
                            ),
                        }"
                        :options="contractAgentCastEligibilityCategoriesData"
                        optionLabel="name"
                        optionValue="code"
                        :placeholder="$t('cand_choose')"
                    />
                    <div
                        v-if="errorMessage('contractAgentCastEligibilityCategory', block.name)"
                        class="invalid-feedback"
                    >
                        {{ $t(errorMessage('contractAgentCastEligibilityCategory', block.name)) }}
                    </div>
                </div>
                <div 
                    class="form-group mb-3 required-group d-flex flex-column" 
                    v-if="applicationTypeIsCa"
                >
                    <label for="castNumber" class="form-label block">
                        {{ $t('lib_cast_number') }}
                    </label>
                    <InputNumber 
                        v-model="castEligibility.castApplicantNumber" 
                        inputId="castNumber" 
                        :useGrouping="false" 
                        :showClear="true"
                        min="1"
                        max="99999999"
                        fluid 
                        :class="{
                            'is-invalid': errorMessage(
                                'castApplicantNumber',
                                block.name
                            ),
                        }"
                    />
                    <div
                        v-if="errorMessage('castApplicantNumber', block.name)"
                        class="invalid-feedback"
                    >
                        {{ $t(errorMessage('castApplicantNumber', block.name)) }}
                    </div>
                </div>
            </div>
                
            <!-- <div class="form-group mb-3 required-group" v-if="applicationTypeIsTa">
                <label for="applicantCategory" class="form-label">
                    {{ $t('lib_cast') }}
                </label>
                <div
                    class="d-flex flex-wrap gap-3"
                    :class="{
                        'is-invalid': errorMessage('category', block.name),
                    }"
                >
                    <template
                        v-for="(category, index) in castEligibilityCategories"
                    >
                        <div
                            v-if="category?.code"
                            class="d-flex align-items-center"
                            :key="index"
                        >
                            <Checkbox
                                v-model="castEligibility.categories"
                                :id="`category-${index}`"
                                :inputId="`category-${index}`"
                                :value="category.code"
                                @change="onEligibilityCategoryChange"
                            />
                            <label class="ms-2" :for="`category-${index}`">
                                {{ category?.name }}
                            </label>
                        </div>
                    </template>
                </div>
                <div
                    v-if="errorMessage('category', block.name)"
                    class="invalid-feedback"
                >
                    {{ $t(errorMessage('category', block.name)) }}
                </div>
            </div> -->
            <div
                class="document-info-disclaimer mt-3 d-flex align-items-center"
            >
                <font-awesome-icon icon="fas fa-info-circle" class="fs-3 text-primary me-2"/>
                <div class="document-disclaimer-text">
                    <p class="mb-0">
                        {{ $t('lib_upload_info') }}
                    </p>
                    <p class="mb-0">
                        {{ $t('lib_doc_rules') }}
                    </p>
                </div>
            </div>
            <template v-for="(doc, index) in castEligibility.documents">
                <div
                    :key="index"
                    v-if="showFileUpload(doc)"
                    class="form-group document-uploader-wrapper mb-3"
                    :class="{
                        'required-group': isDocRequired(doc),
                    }"
                >
                    <div class="mb-3">
                        <label
                            :for="`fileUpload-${index}`"
                            class="mb-1 form-label"
                        >
                            {{ $t(doc.name) }}
                        </label>
                        <p
                            v-if="$te(`${doc.name}_info`)"
                            class="small text-muted"
                        >
                            {{ $t(`${doc.name}_info`) }}
                        </p>
                        <div
                            :class="{
                                'is-invalid': errorMessage(
                                    'document',
                                    block.name,
                                    'documents',
                                    index
                                ),
                            }"
                        >
                            <FileUpload
                                :name="`${doc.name}[]`"
                                :showUploadButton="false"
                                :showCancelButton="false"
                                :fileLimit="1"
                                :customUpload="true"
                                :maxFileSize="10000000"
                                @uploader="uploadDoc($event, doc, index)"
                                @select="uploadDoc($event, doc, index)"
                                accept="image/jpeg, image/jpg, application/pdf"
                                :ref="`fileUpload-${index}`"
                                :id="`fileUpload-${index}`"
                                required
                            >
                                <template #empty>
                                    <p>
                                        <font-awesome-icon
                                            icon="far fa-cloud-upload"
                                            class="me-1 fs-3"
                                        />
                                        {{ $t(`lib_drop`) }}

                                        <button
                                            type="button"
                                            @click="selectDocument(index)"
                                            class="btn btn-link p-0"
                                        >
                                            {{ $t(`lib_browse_doc`) }}
                                        </button>
                                    </p>
                                </template>
                            </FileUpload>
                        </div>
                        <div
                            v-if="
                                errorMessage(
                                    'document',
                                    block.name,
                                    'documents',
                                    index
                                )
                            "
                            class="invalid-feedback"
                        >
                            {{
                                $t(
                                    errorMessage(
                                        'document',
                                        block.name,
                                        'documents',
                                        index
                                    )
                                )
                            }}
                        </div>
                    </div>
                </div>
                <div
                    :key="index"
                    v-if="doc.document"
                    class="form-group uploaded-document-wrapper mb-3"
                    :class="{
                        'required-group': isDocRequired(doc),
                    }"
                >
                    <label for="schoolName" class="form-label">
                        {{ $t(doc.name) }}
                    </label>
                    <div
                        class="uploaded-document mb-3 d-flex align-items-center"
                    >
                        <Avatar
                            shape="circle"
                            size="large"
                            class="bg-light border document-icon me-3 p-3"
                            v-tooltip.top.hover.focus="$t('lib_doc_download')"
                            @click="downloadDoc(doc)"
                            :icon="fileIcon(doc?.document?.extension)"
                            aria-role="button"
                            :aria-label="$t('lib_doc_download')"
                        />
                        <div class="document-details me-md-5">
                            <p
                                class="fw-bold mb-0 text-uppercase document-name"
                                v-tooltip.top.hover="$t('lib_doc_download')"
                                @click="downloadDoc(doc)"
                            >
                                {{ doc?.document?.name }}
                            </p>
                            <p
                                v-tooltip.top.hover="$t('lib_upload_date')"
                                class="mb-0 small fst-italic text-muted"
                            >
                                {{ formatDate(doc?.document?.uploadDate) }}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="download-document-btn btn btn-text px-6 py-1"
                            @click="downloadDoc(doc)"
                            v-tooltip.top.hover.focus="$t('lib_doc_download')"
                        >
                            <font-awesome-icon icon="far fa-download"/>
                        </button>
                        <button
                            type="button"
                            class="replace-documents-btn btn btn-text px-6 py-1 ms-auto ms-sm-0"
                            @click="deleteDoc($event, doc?.document?.id, doc)"
                            v-tooltip.top.hover.focus="$t('lib_remove_doc')"
                        >
                            <font-awesome-icon icon="fas fa-trash"/>
                        </button>
                    </div>
                </div>
            </template>
            <div class="card-footer d-flex justify-content-end mt-3"  v-if="formWasChanged">
                <div class="card-actions">
                    <button
                        type="button"
                        class="card-action save-action btn btn-text me-2"
                        @click="validate()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="far fa-save" class="fs-5 btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_save') }}
                        </span>
                    </button>
                    <button
                        type="button"
                        class="card-action undo-changes-action btn btn-text"
                        @click="undoChanges()"
                        :disabled="appLoading"
                    >
                        <font-awesome-icon icon="fas fa-undo"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
// import Dropdown from 'primevue/dropdown';
import _ from 'lodash';
import Avatar from 'primevue/avatar';
import FileUpload from 'primevue/fileupload';
import Message from 'primevue/message';
import CommonMixins from '../../mixins';
import Dropdown from 'primevue/dropdown';
import InputNumber from 'primevue/inputnumber';

export default {
    name: 'CastEligibility',
    components: {
        // Dropdown,
        FileUpload,
        Avatar,
        Message,
        Dropdown,
        InputNumber
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            castEligibility: null,
            listenToClickOutside: false,
            formWasChanged: false,
            contractAgentCastEligibilityCategoriesData: null
        };
    },
    computed: {
        ...mapGetters([
            'currentSite',
            'studyFields',
            'studyLevels',
            'wizardErrors',
            'blockErrorMessage',
            'errorMessage',
            'isFieldRequired',
            'castEligibilityData',
            'countries',
            'applicationTypeIsTrainee',
            'minStartDateValue',
            'appLoading',
            'europeanInstitutions',
            'documentsData',
            'castEligibilityCategories',
            'cycleApplicantCategory',
            'applicationTypeIsCa',
            'applicationTypeIsTa',
            'isEESCSite'
        ]),
        showWarning() {
            // TODO: - we must see if we have a criteria that will disqualify the candidate straigth forward as we have for OFFFICIAL appType
            let result = false;
            
            return result;
        },
    },
    methods: {
        ...mapActions([
            'validateBlock',
            'updateBlock',
            'clearBlockErrors',
            'focusOnBlock',
            'uploadDocument',
            'downloadDocument',
            'deleteDocument',
            'loadContractAgentCastEligibilityCategories'
        ]),
        validate() {
            this.clearBlockErrors(this.block.name);
            this.validateBlock({
                blockData: { ...this.castEligibility },
                blockName: this.block.name,
            }).then(
                () => {
                    // success
                    this.updateBlock({
                        blockName: this.block.name,
                        application: {
                            applicationId: this.applicationId,
                            applicationType: this.applicationType,
                            applicationBlocks: {
                                [this.block.name]: this.castEligibility,
                            },
                        },
                    }).then(
                        () => {
                            // success
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'success',
                                detail: this.$t('mess_save_success'),
                                life: 3000,
                            });
                            this.loadData();
                            this.$emit('checkErrorForOtherBlocks');
                        },
                        () => {
                            // error
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'error',
                                detail: this.$t('mess_save_failed'),
                                life: 3000,
                            });
                        }
                    );
                },
                () => {
                    // error
                    this.listenToClickOutside = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('mess_save_failed'),
                        life: 3000,
                    });
                }
            );
        },
        onClickOutside() {
            if (
                this.listenToClickOutside &&
                this.formWasChanged &&
                !this.appLoading
            ) {
                this.clearBlockErrors(this.block.name);
                this.validate();
            }
            this.focusOnBlock(null);
        },
        undoChanges() {
            this.loadData();
            this.clearBlockErrors(this.block.name);
        },
        loadData() {
            this.castEligibility = _.cloneDeep(this.castEligibilityData);
            this.loadContractAgentCastEligibilityCategories().then((res) => {
                this.contractAgentCastEligibilityCategoriesData = res.data;
            });
        },
        focusBlock() {
            this.listenToClickOutside = true;
            this.focusOnBlock({
                name: 'castEligibility',
                label: this.$t('lib_supporting_documents'),
            });
        },

        // DOCUMENTS METHODS : START
        selectDocument(index) {
            var fileUpload = this.$refs[`fileUpload-${index}`][0];
            fileUpload.choose();
        },
        uploadDoc(event, doc, index) {
            const formData = new FormData();
            formData.append('file', event.files[0]);
            formData.append('documentTypeId', doc.id);
            formData.append('applicationId', this.applicationId);

            this.uploadDocument(formData).then(
                (response) => {
                    this.$toast.add({
                        severity: 'success',
                        detail: this.$t('lib_action_success', [
                            this.$t('lib_doc_upload'),
                        ]), // "Document uploaded succesfully!",
                        life: 3000,
                    });
                    doc.document = response?.data;
                },
                (err) => {
                    console.error(err);
                    var fileUpload = this.$refs[`fileUpload-${index}`][0];
                    fileUpload.clear();
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: err.detail
                            ? this.$t(err.detail)
                            : `${this.$t('lib_action_failed', [
                                  this.$t('lib_doc_upload'),
                              ])}`, // "There was a problem uploading your file, please try again later.",
                        life: 3000,
                    });
                }
            );
        },
        downloadDoc(doc) {
            // this methods refers to the event triggered by the download btn;
            const docId = doc?.document?.id;
            this.downloadDocument(docId).then(
                (res) => {
                    this.downloadFile(
                        res,
                        doc.document.name,
                        this.$refs.castEligibilityForm
                    );
                    this.$toast.add({
                        severity: 'success',
                        detail: this.$t('lib_action_success', [
                            this.$t('lib_doc_download'),
                        ]), //"Document downloaded succesfully!",
                        life: 3000,
                    });
                },
                (err) => {
                    console.error(err);
                    // here should be error notification;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('lib_action_failed', [
                            this.$t('lib_doc_download'),
                        ]), //"There was a problem downloading your file, please try again later."
                        life: 3000,
                    });
                }
            );
        },
        downloadFile(res, name, target) {
            // this method refers to the event of downloading the file after we recieve the response from the api;
            let fileBlob = window.URL.createObjectURL(new Blob([res.data])),
                virtualFile = document.createElement('a');

            virtualFile.href = fileBlob;
            virtualFile.setAttribute('download', name);
            let appendTarget = target ? target : document.body;
            appendTarget.appendChild(virtualFile);
            virtualFile.click();
            appendTarget.removeChild(virtualFile);
        },
        deleteDoc(event, docId, doc) {
            // console.log('eve', event.currentTarget);
            this.$confirm.require({
                target: event.currentTarget,
                message: this.$t('status_incomplete_alert'), //"Are you sure you want to proceed?",
                icon: 'pi pi-exclamation-triangle text-warning',
                accept: () => {
                    //callback to execute when user confirms the action
                    this.deleteDocument(docId).then(
                        () => {
                            // this.loadPageData();
                            this.$toast.add({
                                severity: 'success',
                                // TODO - lib_action_success The {0} action completed successfully.
                                detail: this.$t('lib_action_success', [
                                    this.$t('lib_doc_remove'),
                                ]), //"Document deleted succesfully!",
                                life: 3000,
                            });
                            doc.document = null;
                        },
                        (err) => {
                            console.error(err);
                            // here should be error notification;
                            this.$toast.add({
                                severity: 'error',
                                // TODO - lib_action_failed The {0} action completed successfully.

                                detail: this.$t('lib_action_failed', [
                                    this.$t('lib_doc_remove'),
                                ]), // "There was a problem uploading your file, please try again later.",
                                life: 3000,
                            });
                        }
                    );
                },
            });
        },
        showFileUpload(doc) {
            let result = !doc?.document;
            // switch (doc?.code?.toLowerCase()) {
            //     case 'epso':
            //         // console.log(
            //         //     'epso switch',
            //         //     this.showEpso(this?.castEligibility?.category),
            //         //     !doc.document, doc
            //         // );
            //         result =
            //             this.showEpso(this?.castEligibility?.category) &&
            //             !doc.document;
            //         break;

            //     case 'proofgrade':
            //         // console.log('proofi switch');
            //         result =
            //             this.isEuOfficial(this?.castEligibility?.category) &&
            //             this.castEligibility?.organisationCode &&
            //             this.castEligibility?.organisationCode?.toLowerCase() !==
            //                 this.currentSite &&
            //             !doc.document;
            //         break;
            // }
            return result;
        },
        isDocRequired(doc) {
            let result = doc?.isRequired;
            switch (doc?.code?.toLowerCase()) {
                case 'proofgrade':
                    result =
                        this.isEuOfficial(this?.castEligibility?.category) &&
                        this.castEligibility?.organisationCode &&
                        this.castEligibility?.organisationCode?.toLowerCase() !==
                            this.currentSite;
                    break;
            }
            return result;
        },
        isEuOfficial(category) {
            return category?.toLowerCase() === 'official';
        },
        showEpso(category) {
            const acceptedValues = ['epso', 'cert', 'grade'];
            return acceptedValues.includes(category?.toLowerCase());
        },
        fileIcon(extension) {
            let fileIcon;
            switch (extension) {
                case '.pdf':
                    fileIcon = 'pi pi-file-pdf';
                    break;
                case '.jpg':
                case '.jpeg':
                    fileIcon = 'pi pi-image';
                    break;
                default:
                    fileIcon = 'pi pi-file';
                    break;
            }
            return fileIcon;
        },
        onEligibilityCategoryChange() {
            this.clearBlockErrors(this.block.name);
        },
    },
    mounted() {
        this.loadData();
    },
    watch: {
        // whenever study object changes, this function will run and this way we will now if the form data was changed so we can save changes;
        castEligibility: {
            handler(newData) {
                const storeData = {
                    ...this.castEligibilityData,
                };
                this.formWasChanged =
                    JSON.stringify(newData) !== JSON.stringify(storeData);

                // if (!this.formWasChanged) {
                //     this.clearBlockErrors(this.block.name);
                // }
            },
            deep: true,
        },
    },
    mixins: [CommonMixins],
};
</script>
<style lang="scss">
.cast-eligibility-card {
    .p-fileupload-buttonbar {
        display: none;
    }
}
.document-info-disclaimer{
    .fa-info-circle{
        color: #2C4390;
    }

    .document-disclaimer-text{
        color: #000;
    }
}
</style>
